import React, { ReactElement, useMemo, useCallback, useEffect, useRef } from "react";
import { QualityHubTaskFormProps, QualityHubTaskFormStateProps } from "./QualityHubTaskForm.types";
import { FormSection, FormControl } from "src/ui/components/Forms";
import { TextField, Dropdown, IDropdownOption, Checkbox, PrimaryButton } from "@fluentui/react";
import { localize } from "src/l10n";
import { useSelector, useDispatch } from "react-redux";
import { Conditional } from "src/components/Conditional";
import { queryResourceTags } from "src/redux";
import { DaysSelector } from "src/components/DaysSelector";
import { SpintrTypes } from "src/typings";

function getVerificationTypeOptions(): IDropdownOption<number>[] {
    return [
        {
            key: SpintrTypes.QualityHubVerificationType.None,
            title: localize("Ingen"),
            text: localize("Ingen"),
            data: SpintrTypes.QualityHubVerificationType.None,
        },
        {
            key: SpintrTypes.QualityHubVerificationType.Image,
            title: localize("Bild"),
            text: localize("Bild"),
            data: SpintrTypes.QualityHubVerificationType.Image,
        },
        {
            key: SpintrTypes.QualityHubVerificationType.Video,
            title: localize("Video"),
            text: localize("Video"),
            data: SpintrTypes.QualityHubVerificationType.Video,
        },
    ];
}

/**
 * QualityHubTaskForm component
 *
 * @param {QualityHubTaskFormProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubTaskForm({
    isActionForm = false,
    isNew = true,
    isCustomizationForm = false,
    canEditContentStatus = false,
    model,
    onModelChange,
    disableGroupCreation,
}: QualityHubTaskFormProps): ReactElement | null {
    const dispatch = useDispatch();
    const inputRef = useRef<HTMLInputElement>(null);
    const l10n = useMemo(() => ({
        description: localize("Beskrivning"),
        title: localize("Titel"),
    }), []);

    const {
        groups,
        tags,
    } = useSelector<Spintr.AppState, QualityHubTaskFormStateProps>(
        (appState) => ({
            groups: appState.qualityHub.listsById[model.listId]?.groups || [],
            tags: appState.qualityHub.allTagIds.map(
                (tagId) => appState.qualityHub.resourceTagsByIds[tagId],
            ) || [],
        }),
    );

    const verificationOptions = useMemo(getVerificationTypeOptions, []);

    const selectedGroup = useMemo(() => model.groupId
        ? groups.find((group) => group.id === model.groupId)
        : undefined, [groups, model.groupId]);

    const tagOptions = useMemo<IDropdownOption[]>(
        () => tags.map((tag) => ({
            key: tag.id,
            text: tag.name,
        })),
        [tags]);

    const groupOptions = useMemo<IDropdownOption[]>(
        () => (!disableGroupCreation ? [{ key: 0, text: localize("SkapaNy"), }] : []).concat(
            groups.map((group) => {
                let text = group.name;

                // add start and end times if they exist
                // examples:
                // 1. "Group name (07:00 - 08:00)" - if both start and end dates are set
                // 2. "Group name (07:00 - )" - if end date is not set
                // 3. "Group name ( - 08:00)" - if start date is not set

                if (group.startTime || group.endTime) {
                    text += " (";

                    if (group.startTime) {
                        text += group.startTime.substr(0, 5); // format as HH:mm
                    }

                    text += " - ";

                    if (group.endTime) {
                        text += group.endTime.substr(0, 5); // format as HH:mm
                    }

                    text += ")";
                }

                return {
                    key: group.id,
                    text,
                };
            }),
        ),
        [groups]);
        

    const onTitleChanged = useCallback(
        (_: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
            onModelChange((model) => ({
                ...model,
                title: newValue ?? "",
            }));
        }
        , [onModelChange]);

    const onDescriptionChanged = useCallback(
        (_: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
            onModelChange((model) => ({
                ...model,
                description: newValue ?? "",
            }));
        }
        , [onModelChange]);

    const onGroupNameChanged = useCallback(
        (_: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
            onModelChange((model) => ({
                ...model,
                groupName: newValue ?? "",
            }));
        } , [onModelChange]);

    const onGroupChanged = useCallback(
        (_: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
            onModelChange((model) => ({
                ...model,
                groupId: option?.key as number,
            }));
        }
        , [onModelChange]);

    const onTagsChanged = useCallback(
        (_: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
            if (!option) {
                return;
            }

            onModelChange((model) => ({
                ...model,
                tagIds: option.selected
                    ? model.tagIds.concat(option.key as number)
                    : model.tagIds.filter((tagId) => tagId !== option.key),
            }));
        }, [onModelChange]);

    const onDaysOfWeekChanged = useCallback(
        (daysOfWeek: Spintr.DaysOfWeek) => onModelChange((model) => ({
            ...model,
            daysOfWeek,
        })), [onModelChange]);

    const onGuidingQuestionChanged = useCallback(
        (_: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
            onModelChange((model) => ({
                ...model,
                guidingQuestion: newValue ?? "",
            }));
        }, [onModelChange]);

    const onIsReminderChanged = useCallback(
        (_: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
            onModelChange((model) => ({
                ...model,
                itemType: checked
                    ? SpintrTypes.QualityHubItemType.Reminder
                    : SpintrTypes.QualityHubItemType.Task,
            }));
        }, [onModelChange]);

    const onAllowOverrideChanged = useCallback(
        (_: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
            onModelChange((model) => ({
                ...model,
                allowOverride: !!checked,
            }));
        }, [onModelChange]);

    const onRequireVerificationChanged = useCallback(
        (_: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
            onModelChange((model) => ({
                ...model,
                requireVerification: !!checked,
                verificationType: !checked 
                    ? SpintrTypes.QualityHubVerificationType.None
                    : SpintrTypes.QualityHubVerificationType.Image, // default to Image if enabling
            }));
        }, [onModelChange]);

    const onPublishedChanged = useCallback(
        (_: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
            onModelChange((model) => ({
                ...model,
                contentStatus: checked
                    ? SpintrTypes.ContentStatus.Published
                    : SpintrTypes.ContentStatus.Draft,
            }));
        }, [onModelChange]);

    const onFilesSelected = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = (event.target.files ? Array.from(event.target.files) : [])
            .filter((file) => file.type.startsWith("image/"))
            .map((file) => ({
                file,
                previewUrl: URL.createObjectURL(file),
            }));

        onModelChange((model) => ({
            ...model,
            files: [...(model.files ?? []), ...files],
        }));
    }, [onModelChange]);

    useEffect(() => {
        dispatch(queryResourceTags(0, 100));
    }, []);


    let selectedVerificationTypes = verificationOptions
        .filter((opt) => 
            // bitwise AND to check if the option is selected
            (model.verificationType || 0) & (opt.data as number))
        .map((opt) => opt.key as number);

    if (selectedVerificationTypes.length === 0) {
        selectedVerificationTypes = [SpintrTypes.QualityHubVerificationType.None];
    }

    return (
        <div className="QualityHubTaskForm">
            <FormSection>
                <FormControl>
                    <TextField aria-required={true}
                               className="textField"
                               label={l10n.title}
                               name="title"
                               onChange={onTitleChanged}
                               required={true}
                               value={model.title} />
                </FormControl>
            </FormSection>

            <FormSection>
                <FormControl>
                    <TextField aria-required={false}
                               className="textField"
                               label={l10n.description}
                               multiline={true}
                               name="description"
                               onChange={onDescriptionChanged}
                               required={false}
                               value={model.description} />
                </FormControl>
            </FormSection>

            <Conditional condition={!isActionForm}>
                <FormSection title={localize("Grupp")}>
                    <FormControl>
                        <Dropdown onChange={onGroupChanged}
                                  options={groupOptions}
                                  selectedKey={model.groupId ?? 0} />
                    </FormControl>

                    <Conditional condition={!model.groupId}>
                        <FormControl>
                            <TextField aria-required={true}
                                       className="textField"
                                       name="groupName"
                                       onChange={onGroupNameChanged}
                                       required={true}
                                       value={model.groupName} />
                        </FormControl>
                    </Conditional>
                </FormSection>
            </Conditional>

            <Conditional condition={selectedGroup?.keyConsideration?.length > 0}>
                <FormSection>
                    <FormControl label={selectedGroup?.keyConsideration}>
                        <TextField aria-required={false}
                                   className="textField"
                                   name="guidingQuestion"
                                   onChange={onGuidingQuestionChanged}
                                   value={model.guidingQuestion} />
                    </FormControl>
                </FormSection>
            </Conditional>

            <Conditional condition={!isActionForm && !isCustomizationForm && tagOptions.length > 0}>
                <FormSection>
                    <FormControl label={localize("SynligFor")}>
                        <Dropdown multiSelect={true}
                                  onChange={onTagsChanged}
                                  options={tagOptions}
                                  selectedKeys={model.tagIds} />
                    </FormControl>
                </FormSection>
            </Conditional>

            <Conditional condition={!isActionForm}>
                <FormSection title={localize("QH_TIME_MANAGEMENT")}>
                    <DaysSelector label={localize("SELECT_DAYS")}
                                  multiple={true}
                                  onChange={onDaysOfWeekChanged}
                                  value={model.daysOfWeek || 0} />
                </FormSection>
            </Conditional>

            <FormSection title={localize("Installningar")} className="settings">
                <Conditional condition={!isActionForm && (!isCustomizationForm || isNew || !!model.forUberId)}>
                    <FormControl>
                        <Checkbox checked={model.itemType === SpintrTypes.QualityHubItemType.Reminder}
                                  label={localize("QH_IS_REMINDER")}
                                  name="isReminder"
                                  onChange={onIsReminderChanged} />
                    </FormControl>

                    <Conditional condition={!isActionForm && !isCustomizationForm}>
                        <FormControl>
                            <Checkbox checked={model.allowOverride}
                                      label={localize("ALLOW_CUSTOMIZATION")}
                                      name="allowOverride"
                                      onChange={onAllowOverrideChanged} />
                        </FormControl>
                    </Conditional>

                </Conditional>

                <Conditional condition={canEditContentStatus && isCustomizationForm}>

                    <FormControl label={localize("Status")}>
                        <Checkbox checked={model.contentStatus === SpintrTypes.ContentStatus.Published}
                                  label={localize("Publicerad")}
                                  name="contentStatus"
                                  onChange={onPublishedChanged} />
                    </FormControl>
                </Conditional>

                <Conditional condition={!isCustomizationForm}>

                    <FormControl label={localize("QH_VERIFICATION")}>
                        <Checkbox checked={model.requireVerification}
                                  disabled={model.itemType === SpintrTypes.QualityHubItemType.Reminder}
                                  label={localize("QH_REQUIRE_VERIFICATION")}
                                  name="requireVerification"
                                  onChange={onRequireVerificationChanged} />
                    </FormControl>
                </Conditional>
            </FormSection>

            <Conditional condition={isActionForm && model.itemType !== SpintrTypes.QualityHubItemType.Reminder}>
                <FormSection title={localize("Bilder")}>
                    <div className="buttons">
                        <PrimaryButton onClick={() => inputRef.current?.click()}>
                            {localize("ValjBilder")}
                        </PrimaryButton>
                        <input accept="image/*"
                               capture="environment"
                               multiple={true}
                               name="files"
                               onChange={onFilesSelected}
                               ref={inputRef}
                               type="file" />
                    </div>
                    <div className="files">
                        {(model.files ?? []).map((upload, index) => (
                            <div className="file" key={index}>
                                <img alt={upload.file.name} src={upload.previewUrl} />
                            </div>
                        ))}
                    </div>
                </FormSection>
            </Conditional>
        </div>
    );
}

export default QualityHubTaskForm;

